import React from "react";
import { Button, Row, Col } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import SearchPlaces from "../SearchPlaces/SearchPlaces";
import { googlePlacesParser } from "../DetailPane/_DetailPane.helpers";
import { l } from "../../libs/localization";

const getFormattedAddress = (data) => {
  if (data && data.address) {
    return (
      <div>
        <p>{data.address || "-"}</p>
        <p>
          {data.CP ? `${data.CP} - ` : ""}
          {data.city}
          {data.province ? ` (${data.province}) - ` : ""}
          {data.region ? `${data.region}, ` : ""}
          {data.nation}
        </p>
      </div>
    );
  }
  return "";
};

const getStringAddress = (data) => {
  if (data && data.address) {
    return `${data.address || ""}, ${data.CP || ""} ${data.city || ""} ${
      data.province || ""
    }${data.region ? ` (${data.region})` : ""}, ${data.nation || ""}`;
  }
  return "";
};

const AddressesList = (props) => {
  const { value, onChange, editing } = props;

  // If value is not an array, initialize it with a single empty address slot
  const addresses = Array.isArray(value) ? value : [
    {
      address: "",
      city: "",
      CP: "",
      province: "",
      region: "",
      nation: "",
      position: null,
      addressNotes: "",
    }
  ];

  const handleAddressChange = (index, parsedAddress) => {
    const updated = [...addresses];
    updated[index] = {
      ...updated[index],
      ...parsedAddress,
    };
    onChange(updated);
  };

  const handleNotesChange = (index, notes) => {
    const updated = [...addresses];
    updated[index] = {
      ...updated[index],
      addressNotes: notes,
    };
    onChange(updated);
  };

  const handleAddAddress = () => {
    const updated = [
      ...addresses,
      {
        address: "",
        city: "",
        CP: "",
        province: "",
        region: "",
        nation: "",
        position: null,
        addressNotes: "",
      },
    ];
    onChange(updated);
  };

  const handleRemoveAddress = (index) => {
    const updated = addresses.filter((_, idx) => idx !== index);
    onChange(updated);
  };

  return (
    <Col span={24} style={{ padding: "10px 0" }}>
      {addresses.length === 0 && !editing && (
        <p style={{ opacity: 0.6 }}>{l("COMMON.NO_DATA")}</p>
      )}
      {addresses.map((addr, idx) => (
        <div
          key={addr._id || idx}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
            backgroundColor: "#fafafa",
            position: "relative",
          }}
        >
          {editing && (
            <button
              type="button"
              onClick={() => handleRemoveAddress(idx)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                border: "none",
                background: "transparent",
                color: "#ff4d4f",
                cursor: "pointer",
                fontSize: "16px",
                zIndex: 10,
              }}
              title={l("COMMON.DELETE")}
            >
              <DeleteOutlined />
            </button>
          )}
          <div style={{ marginBottom: "12px" }}>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "14px",
                color: "#262626",
              }}
            >
              {l("COMMON.ADDRESS")} {addresses.length > 1 ? `#${idx + 1}` : ""}
            </span>
          </div>
          {editing ? (
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: "8px" }}>
                  <b>{l("COMMON.FIND_ADDRESS")}:</b>
                </div>
                <SearchPlaces
                  placeInput={addr.address ? getStringAddress(addr) : ""}
                  searchPlaceholder={l("COMMON.FIND_ADDRESS")}
                  onChange={(gplace) => {
                    if (!gplace) {
                      handleAddressChange(idx, {
                        address: "",
                        city: "",
                        nation: "",
                        CP: "",
                        province: "",
                        region: "",
                        position: null,
                      });
                      return;
                    }
                    const { address_components, geometry } = gplace;
                    const place = googlePlacesParser(address_components);
                    const { location } = geometry || {};

                    if (place && location) {
                      const {
                        street_number,
                        route,
                        locality,
                        postal_town,
                        administrative_area_level_3: hamlet,
                        administrative_area_level_2: province,
                        administrative_area_level_1: region,
                        country: nation,
                        postal_code: CP,
                      } = place;
                      const city = locality || hamlet || postal_town;
                      handleAddressChange(idx, {
                        address: `${route || ""} ${street_number || ""}`,
                        region,
                        province,
                        CP,
                        city,
                        nation,
                        position: {
                          lat: location.lat(),
                          lng: location.lng(),
                        },
                      });
                    }
                  }}
                />
              </Col>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: "8px" }}>
                  <b>{l("COMMON.ADDRESS_NOTE")}:</b>
                </div>
                <textarea
                  className="ant-input"
                  value={addr.addressNotes || ""}
                  placeholder="—"
                  onChange={(e) => handleNotesChange(idx, e.target.value)}
                  rows={2}
                />
              </Col>
            </Row>
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div>{getFormattedAddress(addr)}</div>
              </Col>
              {addr.addressNotes && (
                <Col xs={24} md={12}>
                  <div>
                    <b>{l("COMMON.ADDRESS_NOTE")}:</b>
                    <p style={{ margin: "4px 0 0 0" }}>{addr.addressNotes}</p>
                  </div>
                </Col>
              )}
            </Row>
          )}
        </div>
      ))}

      {editing && (
        <Button
          type="dashed"
          onClick={handleAddAddress}
          block
          style={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderColor: "#d9d9d9",
          }}
        >
          <PlusOutlined /> {l("COMMON.ADD_ADDRESS")}
        </Button>
      )}
    </Col>
  );
};

export default AddressesList;
